import {
  useCreateLessonPlanMutation,
  useUpdateLessonPlanMutation,
} from '@/app/redux/endpoints/lessonPlans';
import { useAppSelector } from '@/app/redux/hooks';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useLessonPlans } from '@/app/redux/hooks/lessonPlans';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import {
  selectIsAuthenticated,
  selectUserId,
} from '@/app/redux/slices/sessionSlice';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import EditorHeader from '@/common/ui/EditorHeader';
import MicroAppNav from '@/common/ui/MicroAppNav';
import LessonPlanCtaModal from '@/common/ui/modals/LessonPlanCtaModal';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { NotFoundError } from '@/common/utils/error';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { useAutosave } from '@/common/utils/useAutosave';
import LessonPlan from '@/dashboard/LessonPlan/LessonPlan';
import { generateInitialContent } from '@/dashboard/LessonPlan/lessonPlanContentGenerators';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

export default function LessonPlanView({
  standalone,
}: {
  standalone?: boolean;
}) {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const navigate = useNavigate();
  const { search } = useLocation();

  const { currentOrganisationId } = useCurrentOrganisationId();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const userId = useAppSelector(selectUserId);

  const {
    lessonPlansById,
    isLoading: plansLoading,
    isFetching: plansFetching,
  } = useLessonPlans();
  const existingPlan = id ? lessonPlansById.get(id) : undefined;

  const { customLevels, isLoading: customLevelsLoading } = useCustomLevels();
  const customLevelId = existingPlan?.customLevelId;
  const customLevel = customLevelId
    ? customLevels.find((l) => l.id === customLevelId)
    : undefined;

  const [createLessonPlan] = useCreateLessonPlanMutation();
  const [updateLessonPlan] = useUpdateLessonPlanMutation();

  const [searchParams] = useSearchParams();
  const levelParam = searchParams.get('level');
  const fromPublic = searchParams.get('from') === 'public';

  const [name, setName] = useState(() => {
    if (existingPlan?.name) return existingPlan.name;
    if (levelParam) return `Lesson Plan: Level ${levelParam}`;
    return 'Untitled Lesson Plan';
  });
  const [content, setContent] = useState('');
  const [worksheetContent, setWorksheetContent] = useState('');
  const [worksheetOutOfSync, setWorksheetOutOfSync] = useState(false);
  const [gameLevelOutOfSync, setGameLevelOutOfSync] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [isReady, setIsReady] = useState(false);
  const [lastSaved, setLastSaved] = useState(existingPlan?.updatedAt);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [isGameLevelSaving, setIsGameLevelSaving] = useState(false);
  const [gameLevelLastSaved, setGameLevelLastSaved] = useState<
    number | undefined
  >(undefined);
  const hasSavedFromPublicRef = useRef(false);

  const { isSaving } = useAutosave({
    saveFn: handleSave,
    trigger: [content, name, worksheetContent],
    disabled: !isReady || standalone,
  });

  // Load content when plan is available
  useEffect(() => {
    if (isNew || standalone) {
      if (fromPublic) {
        const raw = localStorage.getItem('lessonPlanDraft');
        if (raw) {
          const draft = JSON.parse(raw) as { content: string; name: string };
          if (draft.content) setContent(draft.content);
          if (draft.name) setName(draft.name);
        }
      } else if (levelParam) {
        const level = parseInt(levelParam);
        const levelData = scopeAndSequence.levels.find(
          (l) => l.level === level,
        ) as ScopeAndSequenceLevel | undefined;
        if (levelData) setContent(generateInitialContent(levelData));
      }
      setIsReady(true);
      return;
    }

    if (!currentOrganisationId || plansLoading) return;

    if (existingPlan) {
      setContent(existingPlan.content);
      setWorksheetContent(existingPlan.worksheetContent ?? '');
      setIsReady(true);
    } else {
      setIsReady(true);
    }
  }, [
    id,
    existingPlan,
    isNew,
    plansLoading,
    currentOrganisationId,
    levelParam,
    standalone,
    fromPublic,
  ]);

  // Auto-save immediately when landing from public page
  useEffect(() => {
    if (!fromPublic || !isNew || !isReady || !userId || !currentOrganisationId)
      return;
    if (hasSavedFromPublicRef.current) return;
    hasSavedFromPublicRef.current = true;
    handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPublic, isNew, isReady, userId, currentOrganisationId]);

  // Initialise gameLevelLastSaved once when customLevel first loads
  useEffect(() => {
    console.log('customLevelsLoading', customLevelsLoading);
    console.log('customLevel', !!customLevel);
    console.log('gameLevelLastSaved', gameLevelLastSaved);
    if (!customLevelsLoading && customLevel && !gameLevelLastSaved) {
      console.log('setting', customLevel.updatedAt);
      setGameLevelLastSaved(customLevel.updatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customLevel?.updatedAt, customLevelsLoading]);

  // Sync name if existing plan loads after initial render
  useEffect(() => {
    if (existingPlan && name === 'Untitled Lesson Plan') {
      setName(existingPlan.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPlan]);

  function handleContentChange(newContent: string) {
    setContent(newContent);
    if (worksheetContent) setWorksheetOutOfSync(true);
    if (existingPlan?.customLevelId) setGameLevelOutOfSync(true);
  }

  function handleWorksheetRegenerate() {
    setWorksheetOutOfSync(false);
  }

  async function handleSave() {
    if (!userId || !currentOrganisationId) {
      console.error('Missing user ID or org ID');
      toast.error('Failed to save', { description: UNKNOWN_ERROR_MESSAGE });
      return;
    }

    try {
      if (isNew) {
        const newPlan = await createLessonPlan({
          createdBy: userId,
          organisationId: currentOrganisationId,
          name,
          content,
          worksheetContent,
        }).unwrap();
        trackMixpanelEvent('Lesson Plan Created', {
          lessonPlanId: newPlan.id,
          lessonPlanName: name,
          hasLevelParam: !!levelParam,
        });
        localStorage.removeItem('lessonPlanDraft');
        navigate(`/dashboard/lessonplans/${newPlan.id}`);
      } else {
        await updateLessonPlan({
          orgId: currentOrganisationId,
          id: id!,
          updates: { name, content, worksheetContent },
        }).unwrap();
      }
    } catch (error) {
      console.error('Failed to save lesson plan:', error);
      toast.error('Failed to save lesson plan', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setLastSaved(Date.now());
    }
  }

  if (!isReady) return null;

  if (
    !isNew &&
    !standalone &&
    !plansLoading &&
    !plansFetching &&
    !existingPlan
  ) {
    throw new NotFoundError('Lesson plan not found');
  }

  if (standalone) {
    if (isAuthenticated) {
      return <Navigate to={`/dashboard/lessonplans/new${search}`} replace />;
    }

    return (
      <div className="flex flex-col w-full h-screen">
        <MicroAppNav
          feedbackSource="lessonplan"
          helpUrl="https://spellcaster.education/documentation/lesson-plan/lesson-plan-generator"
          ctaContent={
            <Button
              variant="link"
              className="hidden sm:flex items-center gap-1 p-0"
              onClick={() => {
                trackMixpanelEvent('Lesson Plan Save CTA Clicked', {
                  source: 'lessonplan-micronav',
                });
                setCtaOpen(true);
              }}
            >
              Save &amp; edit Leson Plans for free
              <ArrowRightIcon className="size-4" />
            </Button>
          }
        />
        <LessonPlan
          content={content}
          onContentChange={handleContentChange}
          worksheetContent={worksheetContent}
          onWorksheetContentChange={setWorksheetContent}
          worksheetOutOfSync={worksheetOutOfSync}
          onWorksheetRegenerate={handleWorksheetRegenerate}
          gameLevelOutOfSync={gameLevelOutOfSync}
          onGameLevelSync={() => setGameLevelOutOfSync(false)}
          setIsGameLevelSaving={setIsGameLevelSaving}
          setGameLevelLastSaved={setGameLevelLastSaved}
          selectedSectionId={selectedSectionId}
          onSelectedSectionIdChange={setSelectedSectionId}
        />
        <LessonPlanCtaModal
          isOpen={ctaOpen}
          onClose={() => setCtaOpen(false)}
          content={content}
          name={name}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-screen">
      <EditorHeader
        name={name}
        onNameChange={setName}
        isSaving={isSaving || isGameLevelSaving}
        lastSaved={
          lastSaved || gameLevelLastSaved
            ? Math.max(lastSaved ?? 0, gameLevelLastSaved ?? 0)
            : undefined
        }
        backPath="/dashboard/lessonplans"
      />
      <LessonPlan
        lessonPlanId={id}
        lessonPlanName={name}
        content={content}
        onContentChange={handleContentChange}
        worksheetContent={worksheetContent}
        onWorksheetContentChange={setWorksheetContent}
        worksheetOutOfSync={worksheetOutOfSync}
        onWorksheetRegenerate={handleWorksheetRegenerate}
        gameLevelOutOfSync={gameLevelOutOfSync}
        onGameLevelSync={() => setGameLevelOutOfSync(false)}
        setIsGameLevelSaving={setIsGameLevelSaving}
        setGameLevelLastSaved={setGameLevelLastSaved}
        selectedSectionId={selectedSectionId}
        onSelectedSectionIdChange={setSelectedSectionId}
      />
    </div>
  );
}
